(ns core.front.session
  (:require [app.domain.bond.model :as bond-model]
            [app.domain.group.model :as group-model]
            [app.domain.property.model :as property-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [core.front.admin]
            [core.front.analytics :as analytics]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [re-frame.core-instrumented :as rf]))


(rf/reg-sub ::user-data
  (fn [db _]
    (-> db :session/user)))

(defn set-scopes-to-entities [ctx group]
  (-> group
      (update :group/scopes (partial map (partial entities/find-entity ctx)))))

(rf/reg-sub :session/user
  :<- [::user-data]
  :<- [::entities/context]
  (fn [[user-data ctx] _]
    (some-> user-data
            (update :user/authority-groups (partial map
                                                    (partial set-scopes-to-entities ctx))))))

(rf/reg-sub :session/user-id
  :<- [:session/user]
  (fn [{id :db/id} _] id))

(rf/reg-sub :session/logged-in?
  :<- [:session/user]
  :-> some?)

(rf/reg-sub :session/authority-groups
  :<- [:session/user]
  (fn [user _]
    (some-> user :user/authority-groups)))

(rf/reg-sub :session/write-auth-authority-groups
  :<- [:session/authority-groups]
  (fn [authority-groups _]
    (filter #(-> %
                 :group-roles
                 (contains? :group.role/write-auth))
            authority-groups)))

(rf/reg-sub :session/domain-groups
  :<- [:session/user]
  :-> :user/domain-groups)

(rf/reg-sub :session/authority-for-permit-type?
  :<- [:session/authority-groups]
  (fn [authority-groups [_ permit-type]]
    (->> authority-groups
         (mapcat group-model/get-scope-permit-types)
         (some #(= % permit-type)))))

(rf/reg-sub :session/authority-user?
  :<- [:session/authority-groups]
  (fn [authority-groups _]
    (some? (seq authority-groups))))

(defn admin? [user]
  (-> user :user/admin-permissions :permission/admin))

(rf/reg-sub :session/admin?
  :<- [:session/user]
  :-> admin?)

(defn has-role-handler [role]
  (fn [authority-groups _]
    (->> authority-groups
         (map :group-roles)
         (some role))))

(rf/reg-sub :session/inspector-user?
  :<- [:session/authority-groups]
  (has-role-handler :group.role/inspector))

(rf/reg-sub :session/dispatcher-user?
  :<- [:session/authority-groups]
  (has-role-handler :group.role/dispatcher))

(rf/reg-sub :session/clerk-user?
  :<- [:session/authority-groups]
  (has-role-handler :group.role/clerk))

(rf/reg-sub :session/meeting-coordinator-user?
  :<- [:session/domain-groups]
  (has-role-handler :group.role/meeting-coordinator))

(defn- some-has-scope? [permit-type]
  (fn [authority-groups]
    (->> authority-groups
         (mapcat group-model/scopes)
         (some #(-> % :scope/permit-type (= permit-type)))
         some?)))

(rf/reg-sub :session/code-enforcement-authority?
  :<- [:session/write-auth-authority-groups]
  :-> (some-has-scope? :permit-type/CE))

(rf/reg-sub :session/work-orders-authority?
  :<- [:session/write-auth-authority-groups]
  :-> (some-has-scope? :permit-type/PWO))

(rf/reg-sub :session/reviewer-user?
  :<- [:session/authority-groups]
  (has-role-handler :group.role/reviewer))

(rf/reg-sub :session/cashier-user?
  :<- [:session/authority-groups]
  (has-role-handler :group.role/cashier))

(rf/reg-sub :session/bond-admin?
  :<- [:session/user]
  :-> bond-model/user-is-bond-admin?)

(rf/reg-sub :session/property-manager?
  :<- [:session/user]
  :-> property-model/user-is-property-manager?)

(defn property-management-visible?
  "Shared visibility check for subscriptions and event handlers."
  [[pm-feature? property-db-units? admin? property-manager? authority-groups domain-groups]]
  (-> (and pm-feature?
           (or (and property-db-units?
                    (or admin? property-manager? (seq domain-groups)))
               admin?
               property-manager?
               (some #(group-model/has-feature? % :group.feature/property-management)
                     (concat authority-groups domain-groups))))
      boolean))

(rf/reg-sub :session/property-management-visible?
  :<- [::ff/enabled? :feature-flag.id/property-management]
  :<- [::ff/enabled? :feature-flag.id/property-db-units]
  :<- [:session/admin?]
  :<- [:session/property-manager?]
  :<- [:session/authority-groups]
  :<- [:session/domain-groups]
  :-> property-management-visible?)

(defn property-management-visible-from-db?
  "Check Property visibility from app-db and feature flags without reading subscriptions."
  [{{:user/keys [authority-groups domain-groups] :as user} :session/user}]
  (property-management-visible?
    [(ff/enabled? :feature-flag.id/property-management)
     (ff/enabled? :feature-flag.id/property-db-units)
     (admin? user)
     (property-model/user-is-property-manager? user)
     authority-groups
     domain-groups]))

(rf/reg-sub :session/property-management-advanced?
  :<- [:session/admin?]
  :<- [:session/domain-groups]
  (fn [[admin? domain-groups] _]
    (-> (or admin?
            (some #(group-model/has-feature? % :group.feature/property-management)
                  domain-groups))
        boolean)))

(rf/reg-event-fx ::restore-session
  (fn [_ _]
    {:comm/command {:command    :user/whoami
                    :on-success [::restore-session.success]}}))

(rf/reg-event-fx ::restore-session.success
  (fn [{:keys [db]} [_ status {:keys [user feature-flags] :as response}]]
    (ff/reset-flags! feature-flags)
    (analytics/set-user-id user)
    (merge {:db (assoc db :session/user user)}
           (when user
             {:dispatch [::restored-session status response]}))))

(rf/reg-event-fx ::restored-session
  (fn [_ [_ status {:keys [user entities]}]]
    (analytics/set-user-id user)
    {:fx (concat [[:dispatch [::entities/merge-entity-db status (du/entities->entity-db entities)]]]
                 (when (admin? user)
                   [[:dispatch [:core.front.admin/get-authority-groups]]]))}))

(def ^:private session-expired-notification
  (pr-str [:toast
           :warning
           :warning/session-expired
           :warning/session-expired-description
           "notification#session-expired"]))

(rf/reg-fx ::redirect-to-login
  (fn [params]
    (set! js/window.location (href/href :login {} params))))

(rf/reg-event-fx :session/session-expired
  (fn [{{:keys [:route/match :session/user]} :db} _]
    {::redirect-to-login (merge {:go (href/match->href match)}
                                (when user
                                  {:notification session-expired-notification}))}))

(rf/reg-event-fx ::wait-for-user-data
  (fn [{:keys [db]} [_ event]]
    (if (:session/user db)
      {:fx [[:dispatch event]]}
      {:async-flow {:rules [{:when     :seen-any-of?
                             :events   [::restored-session]
                             :dispatch event
                             :halt?    true}]}})))


(defn logout-post [e branding]
  (when e
    (.preventDefault e))
  (let [form           (.createElement js/document "form")
        branding-input (.createElement js/document "input")]
    (set! (.-name branding-input) "branding")
    (set! (.-value branding-input) branding)
    (.append form branding-input)
    (-> js/document .-body (.append form))
    (set! (.-action form) "/api/command/user/logout")
    (set! (.-method form) "post")
    (.submit form)))
